import React, { useState, useMemo } from 'react';
import { Users, HardHat, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function GuildRatioChecker() {
  const [apprentices, setApprentices] = useState<number>(4);
  const [journeymen, setJourneymen] = useState<number>(10);
  const [ratioRule, setRatioRule] = useState<number>(3); // e.g., 3 journeymen per 1 apprentice

  const model = useMemo(() => {
    // How many journeymen do we legally need for the current apprentices?
    const requiredJourneymen = apprentices * ratioRule;
    
    // Are we compliant?
    const isCompliant = journeymen >= requiredJourneymen;

    // What are the exact deltas?
    const journeymenShortfall = isCompliant ? 0 : requiredJourneymen - journeymen;
    
    // If compliant, how many MORE apprentices can we hire before breaking the rule?
    const maxApprenticesAllowed = Math.floor(journeymen / ratioRule);
    const availableApprenticeSlots = isCompliant ? maxApprenticesAllowed - apprentices : 0;

    return {
      requiredJourneymen,
      isCompliant,
      journeymenShortfall,
      maxApprenticesAllowed,
      availableApprenticeSlots,
      currentRatioText: `1 : ${(journeymen / (apprentices || 1)).toFixed(1)}`,
      targetRatioText: `1 : ${ratioRule}`
    };
  }, [apprentices, journeymen, ratioRule]);

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <HardHat className="w-6 h-6 mr-3 text-yellow-500" />
            Apprentice-to-Journeyman Ratio Checker
          </h2>
          <p className="text-slate-300 max-w-xl">
            Ensure your local job site complies with Department of Labor or Union CBA ratio rules. Calculate hiring needs instantly.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Inputs */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Apprentices</label>
              <Input 
                type="number" 
                min="0"
                value={apprentices} 
                onChange={(e) => setApprentices(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Journeymen</label>
              <Input 
                type="number" 
                min="0"
                value={journeymen} 
                onChange={(e) => setJourneymen(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
              <span>Required CBA Ratio</span>
              <span className="font-bold text-slate-900 dark:text-white">1 : {ratioRule}</span>
            </label>
            <input 
              type="range" 
              min="1" 
              max="10" 
              value={ratioRule} 
              onChange={(e) => setRatioRule(Number(e.target.value))}
              className="w-full accent-slate-800"
            />
            <p className="text-xs text-slate-400 mt-2">Requires {ratioRule} Journeymen for every 1 Apprentice on site.</p>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className={`p-6 border-slate-200 dark:border-slate-800 shadow-xl text-white ${model.isCompliant ? 'bg-emerald-700' : 'bg-red-700'}`}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-sm font-bold text-emerald-100 uppercase tracking-wider">Site Compliance Status</h3>
              {model.isCompliant ? <CheckCircle2 className="w-8 h-8 text-emerald-300" /> : <AlertTriangle className="w-8 h-8 text-red-300" />}
            </div>
            
            <div className="text-4xl font-black tracking-tight mb-2">
              {model.isCompliant ? 'Compliant' : 'Non-Compliant'}
            </div>
            <p className="text-emerald-100 text-sm">
              Current Site Ratio is <strong>{model.currentRatioText}</strong>
            </p>
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {model.isCompliant ? (
                <>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-bold text-slate-900 dark:text-white">Available Apprentice Slots</div>
                      <div className="text-sm text-slate-500">You can safely hire this many more</div>
                    </div>
                    <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                      +{model.availableApprenticeSlots}
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                    <div>
                      <div className="font-bold text-slate-900 dark:text-white">Max Capacity Reached</div>
                      <div className="text-sm text-slate-500">Total allowed by {journeymen} Journeymen</div>
                    </div>
                    <div className="text-xl font-bold">
                      {model.maxApprenticesAllowed}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-4 flex items-center justify-between bg-red-50 dark:bg-red-900/10">
                    <div>
                      <div className="font-bold text-red-800 dark:text-red-300">Journeymen Shortfall</div>
                      <div className="text-sm text-red-600 dark:text-red-400">Hire immediately to achieve compliance</div>
                    </div>
                    <div className="text-2xl font-black text-red-600 dark:text-red-400">
                      H-{model.journeymenShortfall}
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                    <div>
                      <div className="font-bold text-slate-900 dark:text-white">Minimum Required</div>
                      <div className="text-sm text-slate-500">To support {apprentices} apprentices</div>
                    </div>
                    <div className="text-xl font-bold text-slate-900 dark:text-white">
                      {model.requiredJourneymen}
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
